import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { CheckCircle2, ChevronRight, Moon } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import type { UseCaseData } from "@/content/use-cases/types";

function FaqJsonLd({ faq }: { faq: UseCaseData["faq"] }) {
  const schema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faq.map((item) => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.answer,
      },
    })),
  };

  return <script type="application/ld+json">{JSON.stringify(schema)}</script>;
}

function BreadcrumbJsonLd({ items }: { items: UseCaseData["breadcrumbs"] }) {
  const schema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.label,
      ...(item.href ? { item: `https://passiveagents.ai${item.href}` } : {}),
    })),
  };

  return <script type="application/ld+json">{JSON.stringify(schema)}</script>;
}

export default function UseCasePage({ data }: { data: UseCaseData }) {
  return (
    <>
      <Helmet>
        <title>{data.seo.title}</title>
        <meta name="description" content={data.seo.description} />
        {data.seo.canonical && (
          <link rel="canonical" href={data.seo.canonical} />
        )}
        <meta property="og:title" content={data.seo.title} />
        <meta property="og:description" content={data.seo.description} />
        {data.seo.canonical && (
          <meta property="og:url" content={data.seo.canonical} />
        )}
      </Helmet>

      <FaqJsonLd faq={data.faq} />
      <BreadcrumbJsonLd items={data.breadcrumbs} />

      <article className="mx-auto max-w-3xl px-4 py-8 sm:px-6 sm:py-12">
        {/* Breadcrumbs */}
        <Breadcrumb className="mb-6">
          <BreadcrumbList>
            {data.breadcrumbs.map((crumb, i) => {
              const isLast = i === data.breadcrumbs.length - 1;
              return (
                <BreadcrumbItem key={crumb.label}>
                  {isLast ? (
                    <BreadcrumbPage>{crumb.label}</BreadcrumbPage>
                  ) : (
                    <>
                      <BreadcrumbLink asChild>
                        <Link to={crumb.href!}>{crumb.label}</Link>
                      </BreadcrumbLink>
                      <BreadcrumbSeparator>
                        <ChevronRight className="h-4 w-4" />
                      </BreadcrumbSeparator>
                    </>
                  )}
                </BreadcrumbItem>
              );
            })}
          </BreadcrumbList>
        </Breadcrumb>

        {/* H1 + Short Answer */}
        <header className="mb-12">
          <h1 className="font-mono text-3xl font-bold text-foreground md:text-4xl">
            {data.h1}
          </h1>
          <p className="mt-4 text-base leading-relaxed text-muted-foreground sm:text-lg">
            {data.shortAnswer}
          </p>
        </header>

        {/* Who This Is For */}
        <section className="mb-12">
          <h2 className="font-mono text-sm font-semibold uppercase tracking-widest text-muted-foreground">
            {data.whoThisIsFor.headline}
          </h2>
          <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {data.whoThisIsFor.audiences.map((audience) => (
              <div
                key={audience.type}
                className="rounded-xl border border-border bg-card p-5"
              >
                <h3 className="font-mono text-sm font-semibold text-foreground">
                  {audience.type}
                </h3>
                <p className="mt-2 text-sm text-muted-foreground">
                  {audience.description}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* Common Problems */}
        <section className="mb-12">
          <h2 className="font-mono text-sm font-semibold uppercase tracking-widest text-muted-foreground">
            Common problems
          </h2>
          <div className="mt-4 space-y-4">
            {data.commonProblems.map((problem) => (
              <div
                key={problem.problem}
                className="rounded-xl border border-border bg-card p-5"
              >
                <h3 className="font-medium text-foreground">
                  {problem.problem}
                </h3>
                <p className="mt-1.5 text-sm text-muted-foreground">
                  {problem.detail}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* How PassiveAgents Helps */}
        <section className="mb-12">
          <h2 className="font-mono text-sm font-semibold uppercase tracking-widest text-muted-foreground">
            How PassiveAgents helps
          </h2>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            {data.howPassiveAgentsHelps.map((item) => (
              <div
                key={item.feature}
                className="rounded-xl border border-border bg-card p-5"
              >
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-400" />
                  <div>
                    <h3 className="text-sm font-medium text-foreground">
                      {item.feature}
                    </h3>
                    <p className="mt-1 text-sm text-muted-foreground">
                      {item.benefit}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Example Workflows */}
        <section className="mb-12">
          <h2 className="font-mono text-sm font-semibold uppercase tracking-widest text-muted-foreground">
            Example workflows
          </h2>
          <div className="mt-4 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {data.exampleWorkflows.map((workflow) => (
              <div
                key={workflow.title}
                className="rounded-xl border border-border bg-card p-5"
              >
                <h3 className="mb-3 font-mono text-xs font-semibold uppercase tracking-wider text-foreground">
                  {workflow.title}
                </h3>
                <ol className="space-y-2">
                  {workflow.steps.map((step, i) => (
                    <li
                      key={step}
                      className="flex gap-2 text-sm text-muted-foreground"
                    >
                      <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-medium text-primary">
                        {i + 1}
                      </span>
                      {step}
                    </li>
                  ))}
                </ol>
              </div>
            ))}
          </div>
        </section>

        {/* What Humans Still Review */}
        <section className="mb-12">
          <h2 className="font-mono text-sm font-semibold uppercase tracking-widest text-muted-foreground">
            What humans still review
          </h2>
          <div className="mt-4 rounded-xl border border-border bg-card p-5">
            <p className="mb-4 text-sm text-muted-foreground">
              Agents handle the implementation. You handle the judgment calls.
              These decisions stay with your team:
            </p>
            <ul className="space-y-2">
              {data.whatHumansReview.map((item) => (
                <li
                  key={item}
                  className="flex items-start gap-2 text-sm text-foreground"
                >
                  <Moon className="mt-0.5 h-4 w-4 shrink-0 text-yellow-400" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Why PassiveAgents Is Different */}
        <section className="mb-12">
          <h2 className="font-mono text-sm font-semibold uppercase tracking-widest text-muted-foreground">
            Why PassiveAgents is different
          </h2>
          <div className="mt-4 space-y-4">
            {data.whyDifferent.map((item) => (
              <div
                key={item.point}
                className="rounded-xl border border-border bg-card p-5"
              >
                <h3 className="font-medium text-foreground">{item.point}</h3>
                <p className="mt-1.5 text-sm text-muted-foreground">
                  {item.detail}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* Setup Options */}
        <section className="mb-12">
          <h2 className="font-mono text-sm font-semibold uppercase tracking-widest text-muted-foreground">
            Setup options
          </h2>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            {data.setupOptions.map((option) => (
              <div
                key={option.title}
                className="flex flex-col rounded-xl border border-border bg-card p-5"
              >
                <h3 className="font-mono text-sm font-semibold text-foreground">
                  {option.title}
                </h3>
                <p className="mt-1 flex-1 text-sm text-muted-foreground">
                  {option.description}
                </p>
                <Button className="mt-4 w-full" asChild>
                  <Link to={option.href}>{option.cta}</Link>
                </Button>
              </div>
            ))}
          </div>
          <p className="mt-3 text-center text-xs text-muted-foreground">
            <Link
              to="/docs?tab=setup"
              className="underline underline-offset-2 hover:text-foreground"
            >
              See full setup guide
            </Link>
            {" \u00B7 "}
            <Link
              to="/pricing"
              className="underline underline-offset-2 hover:text-foreground"
            >
              Compare plans
            </Link>
          </p>
        </section>

        {/* FAQ */}
        <section className="mb-12">
          <h2 className="font-mono text-sm font-semibold uppercase tracking-widest text-muted-foreground">
            FAQ
          </h2>
          <div className="mt-4 divide-y divide-border rounded-lg border border-border bg-card">
            <Accordion type="single" collapsible>
              {data.faq.map((item, index) => (
                <AccordionItem
                  key={item.question}
                  value={`faq-${index}`}
                  className="border-0 px-5"
                >
                  <AccordionTrigger className="text-left text-sm font-medium text-foreground hover:no-underline">
                    {item.question}
                  </AccordionTrigger>
                  <AccordionContent className="whitespace-pre-wrap text-sm text-muted-foreground">
                    {item.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        {/* CTA */}
        <section className="rounded-xl border border-primary/20 bg-primary/5 p-8 text-center sm:p-10">
          <h2 className="font-mono text-2xl font-bold text-foreground">
            {data.cta.headline}
          </h2>
          <p className="mx-auto mt-3 max-w-lg text-sm text-muted-foreground">
            {data.cta.description}
          </p>
          <Button className="mt-6" size="lg" asChild>
            <Link to={data.cta.buttonHref}>{data.cta.buttonText}</Link>
          </Button>
        </section>
      </article>
    </>
  );
}
